<template>
  <div class="page-wrapper">
    <HeaderComponent />
    <div class="page-content">
      <AppBreadcrumb :items="[{ label: $t('roadSigns.breadcrumb') }]" />

      <!-- Search -->
      <div class="search-box">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input v-model="searchQuery" :placeholder="'Belgi nomi yoki raqami...'" @input="handleSearch" />
        <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''; handleSearch()">✕</button>
      </div>

      <!-- Predict Section -->
      <div class="predict-section">
        <div class="predict-card" @click="$refs.fileInput.click()">
          <input ref="fileInput" type="file" accept="image/*" capture="environment" hidden @change="handleFileUpload" />
          <div class="predict-icon">
            <img src="/robot.png" alt="AI Robot" class="robot-img" />
          </div>
          <div class="predict-text">
            <h4>Belgini aniqlash</h4>
            <p>Rasm yuklang yoki kameradan suring</p>
          </div>
        </div>

        <!-- Predict Result -->
        <div v-if="predictLoading" class="predict-result">
          <div class="spinner-ring small"></div>
          <span>Aniqlanmoqda...</span>
        </div>
        <div v-if="predictResult" class="predict-result success">
          <img v-if="predictPreview" :src="predictPreview" class="predict-preview" />
          <div class="predict-info">
            <span class="predict-confidence">{{ predictResult.confidence }}%</span>
            <h4>{{ predictResult.name }}</h4>
            <button v-if="predictResult.sign_code" class="find-btn" @click="findSignByCode(predictResult.sign_code)">Belgini ko'rish →</button>
          </div>
          <button class="clear-btn" @click="predictResult = null; predictPreview = null">✕</button>
        </div>
        <div v-if="predictError" class="predict-result error">
          <span>{{ predictError }}</span>
          <button class="clear-btn" @click="predictError = null">✕</button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-ring"></div>
        <p>Yuklanmoqda...</p>
      </div>

      <!-- Content -->
      <div v-else-if="categories.length">
        <!-- Category tabs -->
        <div class="category-tabs">
          <button
            v-for="cat in categories"
            :key="cat.key"
            class="tab-btn"
            :class="{ active: activeCategory === cat.key }"
            @click="activeCategory = cat.key"
          >
            <span class="tab-icon" v-html="getCategoryIcon(cat.key)"></span>
            <span class="tab-label">{{ cat.name }}</span>
            <span class="tab-count">{{ cat.signs.length }}</span>
          </button>
        </div>

        <!-- Signs grid -->
        <div class="signs-grid" v-if="filteredSigns.length">
          <div
            v-for="sign in filteredSigns"
            :key="sign.id"
            class="sign-card"
            @click="openSign(sign)"
          >
            <div class="sign-image-wrap">
              <img
                :src="sign.image_full_url || sign.image_url"
                :alt="sign.title"
                loading="lazy"
                @error="handleImageError"
              />
            </div>
            <div class="sign-info">
              <span class="sign-code">{{ sign.code }}</span>
              <h4 class="sign-title">{{ sign.title }}</h4>
            </div>
          </div>
        </div>

        <div v-else class="empty-state">
          <p>Hech narsa topilmadi</p>
        </div>
      </div>

      <!-- Error -->
      <div v-else class="empty-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <p>Yo'l belgilari topilmadi</p>
        <button class="retry-btn" @click="fetchSigns">Qayta yuklash</button>
      </div>

      <!-- Detail Modal -->
      <div v-if="selectedSign" class="modal-overlay" @click="selectedSign = null">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="selectedSign = null">✕</button>
          <div class="modal-body">
            <img
              :src="selectedSign.image_full_url || selectedSign.image_url"
              :alt="selectedSign.title"
              class="modal-image"
            />
            <div class="modal-info">
              <span class="sign-code large">{{ selectedSign.code }}</span>
              <h2>{{ selectedSign.title }}</h2>
              <span class="category-badge">{{ selectedSign.category_display }}</span>
              <p v-if="selectedSign.description" class="description">{{ selectedSign.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeaderComponent from '../components/HeaderComponent.vue'
import AppBreadcrumb from '../components/AppBreadcrumb.vue'
import api from '@/api/axios'

export default {
  components: { HeaderComponent, AppBreadcrumb },
  data() {
    return {
      categories: [],
      activeCategory: 'warning',
      searchQuery: '',
      loading: true,
      selectedSign: null,
      predictLoading: false,
      predictResult: null,
      predictPreview: null,
      predictError: null,
    }
  },
  computed: {
    filteredSigns() {
      const cat = this.categories.find(c => c.key === this.activeCategory)
      if (!cat) return []
      if (!this.searchQuery) return cat.signs
      const q = this.searchQuery.toLowerCase()
      return cat.signs.filter(
        s => s.title.toLowerCase().includes(q) || s.code.toLowerCase().includes(q)
      )
    }
  },
  mounted() {
    this.fetchSigns()
  },
  methods: {
    async fetchSigns() {
      this.loading = true
      try {
        const { data } = await api.get('/v1/road-signs/')
        this.categories = data.categories || []
        if (this.categories.length && !this.categories.some(c => c.key === this.activeCategory)) {
          this.activeCategory = this.categories[0].key
        }
      } catch (err) {
        console.error('Road signs error:', err)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        for (const cat of this.categories) {
          const found = cat.signs.some(
            s => s.title.toLowerCase().includes(q) || s.code.toLowerCase().includes(q)
          )
          if (found) {
            this.activeCategory = cat.key
            return
          }
        }
      }
    },
    openSign(sign) {
      this.selectedSign = sign
    },
    handleImageError(e) {
      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="%23999" stroke-width="1.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>'
    },
    getCategoryIcon(key) {
      const icons = {
        warning: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
        priority: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 22 12 12 22 2 12 12 2"/></svg>',
        prohibitory: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>',
        mandatory: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 16 16 12 12 8"/><line x1="8" y1="12" x2="16" y2="12"/></svg>',
        info: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>',
        service: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>',
        additional: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>',
      }
      return icons[key] || '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>'
    },
    async handleFileUpload(e) {
      const file = e.target.files[0]
      if (!file) return
      this.predictLoading = true
      this.predictResult = null
      this.predictError = null

      // Show preview
      this.predictPreview = URL.createObjectURL(file)

      try {
        const formData = new FormData()
        formData.append('file', file)
        const { data } = await api.post('/v1/predict/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (data.success && data.best_match) {
          this.predictResult = data.best_match
        } else {
          this.predictError = 'Belgi aniqlanmadi'
        }
      } catch (err) {
        this.predictError = 'Xatolik yuz berdi: ' + (err.response?.data?.detail || err.message)
      } finally {
        this.predictLoading = false
        this.$refs.fileInput.value = ''
      }
    },
    findSignByCode(code) {
      if (!code) return
      // Find the sign across all categories
      for (const cat of this.categories) {
        const sign = cat.signs.find(s => s.code === code)
        if (sign) {
          this.activeCategory = cat.key
          this.selectedSign = sign
          this.predictResult = null
          this.predictPreview = null
          return
        }
      }
      // Try partial match
      const cleanCode = code.replace(/\.$/, '')
      for (const cat of this.categories) {
        const sign = cat.signs.find(s => s.code.startsWith(cleanCode))
        if (sign) {
          this.activeCategory = cat.key
          this.selectedSign = sign
          this.predictResult = null
          this.predictPreview = null
          return
        }
      }
    }
  }
}
</script>

<style scoped>
.page-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

/* Predict Section */
.predict-section {
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: slideDown 0.5s ease-out;
}
@keyframes slideDown {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
.predict-card {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1));
  border: 1px dashed rgba(99, 102, 241, 0.4);
  border-radius: 20px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}
.predict-card:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(168, 85, 247, 0.15));
  border-color: rgba(99, 102, 241, 0.8);
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -5px rgba(99, 102, 241, 0.2);
}
.predict-icon {
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
}
.robot-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1));
  animation: float 3s ease-in-out infinite;
}
@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
}
.predict-text h4 {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 700;
  background: linear-gradient(135deg, #4f46e5, #9333ea);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.predict-text p {
  margin: 0;
  font-size: 14px;
  color: var(--text-soft, #64748b);
}

.predict-result {
  background: var(--card-bg, #fff);
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  border: 1px solid var(--border-color, #e2e8f0);
}
.predict-result.success { border-left: 4px solid #10b981; }
.predict-result.error { border-left: 4px solid #ef4444; color: #ef4444; }
.predict-preview {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  object-fit: cover;
}
.predict-info { flex: 1; }
.predict-confidence {
  display: inline-block;
  background: #d1fae5;
  color: #059669;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 4px;
}
.predict-info h4 { margin: 0; font-size: 16px; color: var(--text-color, #1e293b); }
.find-btn {
  margin-top: 8px;
  background: transparent;
  border: none;
  color: #4f46e5;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
}
.find-btn:hover { text-decoration: underline; }


/* Search box */
.search-box {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 20px;
  padding: 14px 20px;
  margin-bottom: 30px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}
.search-box:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
  transform: translateY(-1px);
}
.search-box svg { color: var(--text-soft, #94a3b8); flex-shrink: 0; }
.search-box input {
  flex: 1; border: none; background: transparent; font-size: 16px;
  outline: none; color: var(--text-color, #1e293b);
}
.search-box input::placeholder { color: var(--text-soft, #94a3b8); }
.clear-btn {
  background: var(--bg-color, #f1f5f9); border: none; width: 32px; height: 32px;
  border-radius: 50%; cursor: pointer; font-size: 14px; color: var(--text-soft, #64748b);
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s;
}
.clear-btn:hover { background: var(--border-color, #e2e8f0); color: var(--text-color, #0f172a); }

/* Category tabs */
.category-tabs {
  display: flex; gap: 12px; overflow-x: auto; padding-bottom: 16px;
  margin-bottom: 30px; scrollbar-width: none;
}
.category-tabs::-webkit-scrollbar { display: none; }
.tab-btn {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 20px; border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 16px; background: var(--card-bg, #fff);
  cursor: pointer; white-space: nowrap; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 15px; font-weight: 600; color: var(--text-color, #334155);
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}
.tab-btn:hover {
  border-color: #a5b4fc;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(99, 102, 241, 0.1);
}
.tab-btn.active {
  background: linear-gradient(135deg, #6366f1, #a855f7);
  color: white; border-color: transparent;
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3);
}
.tab-icon { font-size: 20px; }
.tab-count {
  background: rgba(0,0,0,0.05); padding: 2px 10px;
  border-radius: 20px; font-size: 13px; font-weight: 700;
}
.tab-btn.active .tab-count { background: rgba(255,255,255,0.25); color: #fff;}

/* Signs grid */
.signs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 24px;
}
.sign-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--border-color, #f1f5f9);
  border-radius: 24px; padding: 24px 16px;
  cursor: pointer; transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex; flex-direction: column; align-items: center;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
  position: relative;
  overflow: hidden;
}
.sign-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; height: 4px;
  background: linear-gradient(90deg, #6366f1, #a855f7);
  opacity: 0; transition: opacity 0.3s ease;
}
.sign-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.12);
  border-color: rgba(99, 102, 241, 0.2);
}
.sign-card:hover::before { opacity: 1; }
.sign-image-wrap {
  width: 120px; height: 120px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 16px;
  transition: transform 0.3s ease;
}
.sign-card:hover .sign-image-wrap {
  transform: scale(1.08);
}
.sign-image-wrap img {
  max-width: 100%; max-height: 100%; object-fit: contain;
  filter: drop-shadow(0 8px 16px rgba(0,0,0,0.1));
}
.sign-info { width: 100%; }
.sign-code {
  display: inline-block; background: rgba(99, 102, 241, 0.1);
  color: #4f46e5; font-size: 13px; font-weight: 800;
  padding: 4px 12px; border-radius: 20px; margin-bottom: 10px;
}
.sign-code.large {
  font-size: 18px; padding: 6px 18px; background: rgba(99, 102, 241, 0.15);
}
.sign-title {
  font-size: 15px; font-weight: 600; margin: 0;
  color: var(--text-color, #1e293b);
  display: -webkit-box; -webkit-line-clamp: 3;
  -webkit-box-orient: vertical; overflow: hidden;
  line-height: 1.4;
}

/* Loading & Empty */
.loading-state, .empty-state { text-align: center; padding: 80px 20px; color: var(--text-soft, #64748b); }
.spinner-ring {
  width: 48px; height: 48px; margin: 0 auto 20px;
  border: 4px solid var(--border-color, #f1f5f9); border-top-color: #6366f1;
  border-radius: 50%; animation: spin 0.8s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}
.spinner-ring.small { width: 24px; height: 24px; border-width: 3px; margin: 0; }
@keyframes spin { to { transform: rotate(360deg); } }

.empty-state svg { margin-bottom: 20px; color: var(--text-soft, #94a3b8); }
.empty-state p { font-size: 18px; font-weight: 500; }
.retry-btn {
  margin-top: 16px; padding: 12px 28px;
  background: linear-gradient(135deg, #6366f1, #a855f7);
  color: white; border: none; border-radius: 14px;
  cursor: pointer; font-weight: 600; font-size: 15px;
  transition: all 0.2s; box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}
.retry-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4); }

/* Modal */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px); z-index: 1000;
  display: flex; align-items: center; justify-content: center;
  animation: fadeIn 0.3s ease;
  padding: 20px;
}
@keyframes fadeIn { from { opacity: 0; } }
.modal-content {
  background: var(--card-bg, white); border-radius: 28px;
  max-width: 500px; width: 100%; max-height: 90vh;
  overflow-y: auto; position: relative; animation: slideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}
@keyframes slideUp { from { transform: translateY(40px) scale(0.95); opacity: 0; } }
.modal-close {
  position: absolute; top: 20px; right: 20px;
  width: 40px; height: 40px; border-radius: 50%;
  background: var(--bg-color, #f1f5f9); border: none;
  font-size: 20px; cursor: pointer; display: flex;
  align-items: center; justify-content: center; z-index: 2;
  transition: all 0.2s; color: var(--text-soft, #64748b);
}
.modal-close:hover { background: var(--border-color, #e2e8f0); color: var(--text-color, #0f172a); transform: rotate(90deg); }
.modal-body { padding: 40px; text-align: center; }
.modal-image {
  max-width: 240px; max-height: 240px; object-fit: contain;
  margin-bottom: 24px; filter: drop-shadow(0 12px 24px rgba(0,0,0,0.15));
}
.modal-info h2 {
  font-size: 24px; font-weight: 800; margin: 12px 0 16px;
  color: var(--text-color, #0f172a); line-height: 1.3;
}
.category-badge {
  display: inline-block; padding: 6px 16px;
  background: linear-gradient(135deg, #6366f1, #a855f7);
  color: white; border-radius: 20px; font-size: 14px;
  font-weight: 600; margin-bottom: 20px;
  box-shadow: 0 4px 10px rgba(99, 102, 241, 0.3);
}
.description {
  font-size: 15px; color: var(--text-soft, #475569); line-height: 1.7;
  text-align: left; margin-top: 16px;
  background: var(--bg-color, #f8fafc); padding: 16px; border-radius: 16px;
  border: 1px solid var(--border-color, #e2e8f0);
}

/* Responsive */
@media (max-width: 768px) {
  .page-content { padding: 16px; }
  .search-box { margin-bottom: 20px; padding: 12px 16px; border-radius: 16px; }
  .category-tabs { margin-bottom: 20px; padding-bottom: 12px; -webkit-overflow-scrolling: touch; }
  .signs-grid { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 14px; }
  
  .predict-section { margin-bottom: 20px; }
  .predict-card { padding: 16px; gap: 14px; border-radius: 16px; }
  .predict-icon { width: 56px; height: 56px; }
  .predict-text h4 { font-size: 16px; }
  .predict-text p { font-size: 13px; }
  
  .modal-content { border-radius: 24px; }
  .modal-body { padding: 24px; }
  .modal-image { max-width: 180px; max-height: 180px; margin-bottom: 20px; }
  .modal-info h2 { font-size: 20px; margin: 10px 0 12px; }
  .category-badge { font-size: 13px; padding: 5px 14px; margin-bottom: 16px; }
  .description { font-size: 14px; padding: 14px; }
}

@media (max-width: 480px) {
  .page-content { padding: 12px; }
  .search-box { margin-bottom: 16px; padding: 10px 14px; font-size: 14px; }
  .search-box svg { width: 18px; height: 18px; }
  .search-box input { font-size: 14px; }
  
  .category-tabs { gap: 8px; margin-bottom: 16px; padding-bottom: 8px; }
  .tab-btn { padding: 8px 14px; font-size: 13px; border-radius: 12px; gap: 6px; }
  .tab-icon { font-size: 16px; }
  .tab-count { font-size: 11px; padding: 2px 8px; }

  .signs-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px; }
  .sign-card { padding: 14px 10px; border-radius: 16px; }
  .sign-image-wrap { width: 80px; height: 80px; margin-bottom: 12px; }
  .sign-code { font-size: 11px; padding: 3px 10px; margin-bottom: 8px; }
  .sign-title { font-size: 12px; -webkit-line-clamp: 2; }
  
  .predict-card { padding: 14px; gap: 12px; flex-direction: row; }
  .predict-icon { width: 48px; height: 48px; flex-shrink: 0; }
  .predict-text h4 { font-size: 15px; margin-bottom: 2px; }
  .predict-text p { font-size: 12px; }
  .predict-result { padding: 12px 14px; gap: 12px; }
  .predict-preview { width: 48px; height: 48px; }
  .predict-info h4 { font-size: 14px; }
  .predict-confidence { font-size: 11px; padding: 2px 6px; }
  
  .modal-overlay { padding: 16px; }
  .modal-content { border-radius: 20px; }
  .modal-close { top: 12px; right: 12px; width: 32px; height: 32px; font-size: 16px; }
  .modal-body { padding: 24px 16px 20px; }
  .modal-image { max-width: 140px; max-height: 140px; margin-bottom: 16px; }
  .modal-info h2 { font-size: 18px; margin: 8px 0 10px; }
  .sign-code.large { font-size: 14px; padding: 4px 12px; }
  .category-badge { font-size: 12px; padding: 4px 12px; margin-bottom: 12px; }
  .description { font-size: 13px; padding: 12px; border-radius: 12px; line-height: 1.5; }
}

/* Dark Mode Overrides for Cards */
[data-theme="dark"] .predict-card,
.dark .predict-card {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(168, 85, 247, 0.15));
  border-color: rgba(99, 102, 241, 0.3);
}
[data-theme="dark"] .predict-result,
.dark .predict-result {
  background: #1e293b;
  border-color: #334155;
}

[data-theme="dark"] .sign-card,
.dark .sign-card {
  background: #1e293b;
  border-color: #334155;
}
[data-theme="dark"] .sign-card:hover,
.dark .sign-card:hover {
  border-color: rgba(99, 102, 241, 0.5);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}
[data-theme="dark"] .sign-title,
.dark .sign-title { 
  color: #f1f5f9; 
}
[data-theme="dark"] .sign-code,
.dark .sign-code {
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
}

[data-theme="dark"] .modal-content,
.dark .modal-content {
  background: #1e293b;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}
[data-theme="dark"] .description,
.dark .description {
  background: #0f172a;
  border-color: #334155;
  color: #cbd5e1;
}

[data-theme="dark"] .search-box,
.dark .search-box {
  background: #1e293b;
  border-color: #334155;
}
[data-theme="dark"] .tab-btn,
.dark .tab-btn {
  background: #1e293b;
  border-color: #334155;
  color: #cbd5e1;
}
[data-theme="dark"] .tab-btn:hover,
.dark .tab-btn:hover {
  border-color: #818cf8;
}
[data-theme="dark"] .tab-btn.active,
.dark .tab-btn.active {
  background: linear-gradient(135deg, #4f46e5, #9333ea);
  border-color: transparent;
  color: #fff;
}
</style>